import { noop } from 'lodash';
import { memo, useMemo } from 'react';

import { Modal } from '@air/primitive-modal';

import { EditSharePrivateCollectionModalHeader } from '~/components/SharePrivateCollectionModal/components/EditSharePrivateCollectionModalHeader';
import { SharePrivateCollectionModalForm } from '~/components/SharePrivateCollectionModal/components/SharePrivateCollectionModalForm';
import { useEditSharePrivateCollection } from '~/components/SharePrivateCollectionModal/hooks/useEditSharePrivateCollection';
import { SharePrivateCollectionModalProvider } from '~/components/SharePrivateCollectionModal/SharePrivateCollectionModalContext';
import { ShareLinkTeamInviteNudgePanel } from '~/components/SharePrivateContentModal/ShareLinkTeamInviteNudgePanel';
import { useShowShareLinkTeamInviteNudgeForModal } from '~/hooks/useShowShareLinkTeamInviteNudgeForModal';
import { useShowSubscriptionExpiredModal } from '~/hooks/useShowSubscriptionExpiredModal';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { canEditCollection } from '~/utils/permissions/workspacePermissions';

export type EditSharePrivateCollectionModalProps = {
  collectionId: string;
  onClose: () => void;
};

export const EditSharePrivateCollectionModal = memo(
  ({ collectionId, onClose }: EditSharePrivateCollectionModalProps) => {
    const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
    const { showingSubscriptionExpiredModal } = useShowSubscriptionExpiredModal({ onClose });

    const props = useEditSharePrivateCollection({ collectionId });

    const canEdit = canEditCollection(workspacePermissions);
    const { isShareLinkTeamInviteNudgeVisible, openShareLinkTeamInviteNudgeModal, workspaceName } =
      useShowShareLinkTeamInviteNudgeForModal({
        isShareLinkCreated: false,
        isShareLinkReady: props.hasShortUrl,
        location: 'collection_share_link',
        onShareLinkCreatedFallback: undefined,
      });

    const contextValue = useMemo(
      () => ({
        ...props,
        isEditMode: true,
        errors: props.validationErrors,
        onSave: noop,
        onClose,
      }),
      [props, onClose],
    );

    if (!canEdit || showingSubscriptionExpiredModal || props.isLoading) {
      return null;
    }

    return (
      <Modal
        data-testid="SHARE_PRIVATE_CONTENT_MODAL"
        className="my-auto bg-transparent p-0"
        overlayClassName="flex min-h-full items-center justify-center"
        isOpen
        onDismiss={onClose}
      >
        <div className="rounded-6 bg-surface-1">
          <SharePrivateCollectionModalProvider value={contextValue}>
            <EditSharePrivateCollectionModalHeader onClose={onClose} />
            <SharePrivateCollectionModalForm />
          </SharePrivateCollectionModalProvider>
        </div>
        {isShareLinkTeamInviteNudgeVisible && (
          <ShareLinkTeamInviteNudgePanel
            onInviteClick={openShareLinkTeamInviteNudgeModal}
            workspaceName={workspaceName}
          />
        )}
      </Modal>
    );
  },
);

EditSharePrivateCollectionModal.displayName = 'EditSharePrivateCollectionModal';
